import { NgForOf } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, Pipe, PipeTransform, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CrmTranslatePipe } from 'common-module/translate';
import { CrmSelectOption } from 'common-module/select';
import { InputButtonGroupComponent } from '@app/shared/components/autocomplete-search-input/input-button-group.component';
import { TagComponent } from '@app/shared/components/tag/tag.component';

export type AutocompleteFunction = (value: string) => Observable<CrmSelectOption[]>;

@Pipe({
  name: 'mapTo',
  standalone: true
})
export class MapToPipe implements PipeTransform {
  public transform(value: string, objects: any[], mapProperty: string): any {
    return (objects ?? []).find((o) => o[mapProperty] === value);
  }
}

@Pipe({
  name: 'concat',
  standalone: true
})
export class ConcatPipe implements PipeTransform {
  public transform(arr1: any[], arr2: any[]): any[] {
    return arr1.concat(arr2);
  }
}

@Component({
  selector: 'app-input-tags',
  template: `
    <div class="input-tags">
      <app-input-group
        class="input-tags__input-group"
        [btnLabel]="'generic.addBtnLabel' | crmTranslate"
        [placeholder]="placeholder"
        [autocompleteFunction]="autocompleteFnInternal"
        [(ngModel)]="value"
        (ngModelChange)="addTagFromValue()"
      ></app-input-group>

      <div class="input-tags__tags">
        <app-tag
          *ngFor="let tag of tags"
          [title]="(tag | mapTo: (options | concat: selectedTagOptions) : 'value')?.label"
          (tagClosed)="removeTag(tag)"
        ></app-tag>
      </div>
    </div>
  `,
  styleUrls: ['./input-tags.component.less'],
  imports: [InputButtonGroupComponent, CrmTranslatePipe, FormsModule, TagComponent, NgForOf, MapToPipe, ConcatPipe],
  standalone: true
})
export class InputTagsComponent implements OnChanges {
  @Input() public label?: string;
  @Input() public placeholder?: string;
  @Input() public autocompleteFunction!: AutocompleteFunction;
  @Input() public getTagsForValue!: (tag: CrmSelectOption) => Observable<CrmSelectOption[]>;
  @Output() public tagsChanged = new EventEmitter<CrmSelectOption[]>();
  public value?: CrmSelectOption;
  public autocompleteFnInternal!: AutocompleteFunction;
  public options: CrmSelectOption[] = [];
  public selectedTagOptions: CrmSelectOption[] = [];
  public _tags = new Set<string>();

  public get tags() {
    return Array.from(this._tags);
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes['autocompleteFunction'] && this.autocompleteFunction !== undefined) {
      this.autocompleteFnInternal = (value: string) =>
        this.autocompleteFunction(value).pipe(tap((res) => (this.options = res)));
    }
  }

  public removeTag(tag: string) {
    this._tags.delete(tag);
    this.removeFromTagOptions(tag);
    this.emitChangeEvent();
  }

  public addTagFromValue() {
    if (!this.value) {
      return;
    }

    if (this.value.label[0] === '7') {
      this._tags.add(this.value.value.toString());
      this.selectedTagOptions.push(this.value);
    } else {
      this.getTagsForValue(this.value).subscribe((tagOptions) => {
        tagOptions.forEach((opt) => {
          this._tags.add(opt.value.toString());
          this.selectedTagOptions.push(opt);
        });
      });
    }

    this.value = undefined;
    this.emitChangeEvent();
  }

  private emitChangeEvent() {
    const selectedTags = Array.from(this._tags.values())
      .map((tagValue) => {
        return this.options.find(({ value }) => value === tagValue);
      })
      .filter(Boolean)
      .map((res) => res as CrmSelectOption);
    this.tagsChanged.emit(selectedTags);
  }

  private removeFromTagOptions(tag: string) {
    this.selectedTagOptions = this.selectedTagOptions.filter(({ value }) => value === tag);
  }
}
